import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, UploadCloud, Ruler } from "lucide-react";
import { useRowSelection, FailedSitesNotice, LoadingSitesNotice, usePollWhileLoading } from "@/components/rowSelection";

const rowKey = (r) => `${r.site_id}:${r.product_id}`;

export default function SizePricePanel({ siteIds }) {
  const [data, setData] = useState({ sizes: [], rows: [] });
  const [size, setSize] = useState("");
  const [category, setCategory] = useState("all");
  const [price, setPrice] = useState("");
  const sel = useRowSelection();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const reqId = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const id = ++reqId.current;
    if (!quiet) setLoading(true);
    try {
      const r = await api.get("/catalog/sizes", { params: { site_ids: siteIds.join(",") } });
      if (id === reqId.current) setData(r.data);
    } catch (e) {
      if (id === reqId.current) toast.error(`Couldn't load sizes: ${apiError(e)} — switch tabs or reselect stores to retry`);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [siteIds]);

  useEffect(() => {
    setData({ sizes: [], rows: [] });
    sel.reset();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const quietReload = useCallback(() => load(true), [load]);
  usePollWhileLoading(data.loading_sites, quietReload);

  const sizeRows = useMemo(() => data.rows.filter((r) => r.variation === size), [data, size]);
  const categories = useMemo(() => [...new Set(sizeRows.flatMap((r) => r.categories))].sort(), [sizeRows]);
  const visible = useMemo(
    () => (category === "all" ? sizeRows : sizeRows.filter((r) => r.categories.includes(category))),
    [sizeRows, category]
  );

  const isOn = (r) => sel.isOn(rowKey(r));
  const toggle = sel.toggle;
  const allOn = visible.length > 0 && visible.every(isOn);
  const toggleAll = () => sel.setAll(!allOn);

  const targets = visible.filter(isOn);
  const validPrice = /^\d+(\.\d{1,2})?$/.test(price);
  const [code, setCode] = useState("");
  const [codeConfirm, setCodeConfirm] = useState(false);

  const applyCode = async (value = code.trim()) => {
    setCodeConfirm(false);
    setBusy(true);
    try {
      const r = await api.post("/catalog/set-promo-code", {
        code: value,
        targets: targets.map((t) => ({ site_id: t.site_id, product_id: t.product_id })),
      });
      toast.success(value ? `Promo code "${value}" saved on ${r.data.updated} item${r.data.updated === 1 ? "" : "s"}` : `Promo code removed from ${r.data.updated} item${r.data.updated === 1 ? "" : "s"}`);
      if (r.data.failed) toast.error(`${r.data.failed} could not be updated — try again`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const items = targets.map((r) => ({
        sku: r.sku || "-",
        regular_price: Number(price).toFixed(2),
        targets: [{ site_id: r.site_id, product_id: r.product_id }],
      }));
      const sids = [...new Set(targets.map((r) => r.site_id))];
      const r = await api.post("/catalog/apply-prices", { site_ids: sids, items });
      toast.success(`${size} set to $${Number(price).toFixed(2)} on ${r.data.updated} item${r.data.updated === 1 ? "" : "s"}`);
      setPrice("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="size-price-panel">
      <div className="rounded-lg border border-border bg-card p-4 grid gap-4 md:grid-cols-4 items-end">
        <div className="space-y-2">
          <Label className="flex items-center gap-2"><Ruler className="w-4 h-4 text-primary" /> Size</Label>
          <Select value={size} onValueChange={(v) => { setSize(v); setCategory("all"); setPrice(""); sel.reset(); }}>
            <SelectTrigger data-testid="size-select"><SelectValue placeholder={loading ? "Loading sizes…" : "Choose a size"} /></SelectTrigger>
            <SelectContent>
              {data.sizes.map((s) => (
                <SelectItem key={s.label} value={s.label} data-testid={`size-option-${s.label}`}>
                  {s.label} ({s.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={category} onValueChange={(v) => { setCategory(v); sel.reset(); }} disabled={!size}>
            <SelectTrigger data-testid="size-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>New regular price $</Label>
          <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="23.99" className="font-mono-num" data-testid="size-new-price" />
        </div>
        <Button onClick={() => setConfirm(true)} disabled={busy || loading || !validPrice || targets.length === 0} data-testid="size-apply-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          Set {targets.length} item{targets.length === 1 ? "" : "s"}
        </Button>
      </div>

      {size && (
        <div className="rounded-lg border border-border bg-card p-4 flex flex-wrap items-end gap-3" data-testid="promo-code-bar">
          <div className="space-y-2">
            <Label>Promo code for ticked items</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="pza16" className="w-48 font-mono-num" data-testid="promo-code-input" />
          </div>
          <Button variant="outline" onClick={() => setCodeConfirm(true)} disabled={busy || loading || !code.trim() || targets.length === 0} data-testid="promo-code-apply">
            Save code on {targets.length} item{targets.length === 1 ? "" : "s"}
          </Button>
          <Button variant="ghost" onClick={() => setCodeConfirm("remove")} disabled={busy || loading || targets.length === 0} data-testid="promo-code-remove">
            Remove code
          </Button>
          <p className="text-xs text-muted-foreground max-w-md">
            Saved in each size's WooCommerce Description field. Use the code in Promotion Now or Scheduled promotions. Note: WooCommerce shows this description to customers when they pick the size.
          </p>
        </div>
      )}
      <FailedSitesNotice sites={data.failed_sites} />
      <LoadingSitesNotice sites={data.loading_sites} />
      {size && (
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <div className="overflow-x-auto max-h-[55vh]">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-10">
                    <Checkbox checked={allOn} onCheckedChange={toggleAll} data-testid="size-select-all" />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Promo code</TableHead>
                  <TableHead>Current regular</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={rowKey(r)} className="border-border" data-testid={`size-row-${r.site_id}-${r.product_id}`}>
                    <TableCell>
                      <Checkbox checked={isOn(r)} onCheckedChange={() => toggle(rowKey(r))} data-testid={`size-select-${r.site_id}-${r.product_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">{r.parent_name || r.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.site_name}</TableCell>
                    <TableCell className="font-mono-num text-xs text-muted-foreground">{r.sku || "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs" data-testid={`size-code-${r.site_id}-${r.product_id}`}>{r.promo_code || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm">{r.regular_price || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <AlertDialog open={!!codeConfirm} onOpenChange={(o) => !o && setCodeConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {codeConfirm === "remove" ? `Remove the promo code from ${targets.length} item${targets.length === 1 ? "" : "s"}?` : `Save promo code "${code.trim()}" on ${targets.length} item${targets.length === 1 ? "" : "s"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the Description field of each ticked size in WooCommerce on {new Set(targets.map((r) => r.site_id)).size} store(s). Prices are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promo-code-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyCode(codeConfirm === "remove" ? "" : code.trim())} data-testid="promo-code-confirm">
              {codeConfirm === "remove" ? "Remove code" : "Save code"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set {targets.length} × "{size}" to ${validPrice ? Number(price).toFixed(2) : price}?</AlertDialogTitle>
            <AlertDialogDescription>
              This changes the regular price of every ticked item on {new Set(targets.map((r) => r.site_id)).size} store(s) in WooCommerce.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="size-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={apply} data-testid="size-confirm">Set price</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
